import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import CatalogHiddenBanner from "../components/CatalogHiddenBanner";
import { Button } from "../components/ui/button";
import { useAuth } from "../lib/auth-context";
import { useShop, useShopHref } from "../lib/shop-context";

/**
 * Phase 30 — Full-listing page shared by /destacados, /nuevo and /para-ti.
 *
 * Props:
 *   title            — H1 rendered at the top of the page
 *   subtitle         — optional secondary text
 *   testid           — root data-testid
 *   fetchFn          — async () => products[]. Isolated so each page can plug
 *                      its own endpoint (GET, POST /ai/recommendations, …)
 *   emptyMessage     — text shown when the fetch returns []
 *   authRequired     — when true, redirects to /entrar if no user (used by /para-ti)
 */
export default function ProductListPage({
  title,
  subtitle,
  testid,
  fetchFn,
  emptyMessage = "No hay productos disponibles.",
  authRequired = false,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user, loading: authLoading } = useAuth();
  const shopHref = useShopHref();
  const shopCtx = useShop();

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchFn()
      .then((data) => {
        if (!alive) return;
        setItems(Array.isArray(data) ? data : data?.products || []);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e);
        setItems([]);
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (authRequired && !authLoading && !user) {
    return (
      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-16 text-center">
        <h1 className="text-2xl font-black">Inicia sesión para ver «{title}»</h1>
        <p className="mt-2 text-sm text-neutral-500">
          Personalizamos las recomendaciones para tu cuenta.
        </p>
        <Button
          asChild
          className="mt-6 rounded-full bg-[#00953B] hover:bg-[#007a30]"
          data-testid={`${testid}-login-cta`}
        >
          <Link to="/entrar">Iniciar sesión</Link>
        </Button>
      </div>
    );
  }

  return (
    <div
      data-testid={testid}
      className="mx-auto max-w-7xl px-4 sm:px-6 py-6 sm:py-10"
    >
      {shopCtx?.catalog_hidden ? (
        <CatalogHiddenBanner />
      ) : (
        <>
      <div className="mb-6">
        <Link
          to={shopHref("/")}
          data-testid={`${testid}-back-home`}
          className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-[#00953B]"
        >
          <ChevronLeft className="h-4 w-4" /> Volver al inicio
        </Link>
        <h1 className="mt-3 text-3xl sm:text-4xl font-black tracking-tight">
          {title}
        </h1>
        {subtitle && (
          <p className="mt-1 text-sm text-neutral-500">{subtitle}</p>
        )}
      </div>

      {loading && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
          {Array.from({ length: 12 }).map((_, i) => (
            <div
              key={i}
              className="h-[320px] rounded-xl bg-neutral-100 animate-pulse"
            />
          ))}
        </div>
      )}

      {!loading && items.length === 0 && (
        <div
          data-testid={`${testid}-empty`}
          className="rounded-2xl border border-dashed border-neutral-200 bg-neutral-50 px-6 py-14 text-center text-sm text-neutral-500"
        >
          {error ? "No se han podido cargar los productos." : emptyMessage}
        </div>
      )}

      {!loading && items.length > 0 && (
        <div
          data-testid={`${testid}-grid`}
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4"
        >
          {items.map((p) => (
            <div key={p.id} data-testid={`${testid}-tile-${p.slug}`}>
              <ProductCard product={p} />
            </div>
          ))}
        </div>
      )}
        </>
      )}
    </div>
  );
}
